import type { Ref } from 'vue';
import log from '~/utils/log';

/** Wait after the last keystroke before sending the new quantity. */
export const CART_ITEM_QUANTITY_DEBOUNCE_MS = 800;

type CartItemQuantityOptions = {
    /** The quantity Shopware currently holds for this line item. */
    itemQuantity: Readonly<Ref<number | undefined>>;
    /** Sends the new quantity. Must handle its own request errors. */
    commitQuantity: (quantity: number) => Promise<void>;
    /** Runs after the on-screen quantity has been resynced to the server's. */
    onCommitted?: (settledQuantity: number | undefined) => void;
    debounceMs?: number;
};

/**
 * Owns the editable quantity of a single cart item. Extracted from the component
 * because the debounce, the server resync and the write ordering interact:
 * for up to 800 ms the quantity on screen is not the one Shopware holds, and
 * every cart refresh syncs the server value back, re-firing the watcher on every
 * rendered item.
 *
 * `isBusy` covers the whole edit, keystroke to settled, so the item can lock its
 * own controls. Blocking checkout is `useShopwareWriteState`'s job.
 */
export const useCartItemQuantity = ({
    itemQuantity,
    commitQuantity,
    onCommitted,
    debounceMs = CART_ITEM_QUANTITY_DEBOUNCE_MS
}: CartItemQuantityOptions) => {
    const quantity = ref<number | undefined>(itemQuantity.value);
    syncRefs(itemQuantity, quantity);

    const pendingQuantityEdit = shallowRef(false);

    // Counted, so a second edit cannot make the item look idle between two writes.
    const pendingQuantityWrites = ref(0);
    const isUpdating = computed(() => pendingQuantityWrites.value > 0);

    /** Any edit pending on this item, whether still debouncing or in flight. */
    const isBusy = computed(() => isUpdating.value || pendingQuantityEdit.value);

    let quantityWriteQueue: Promise<void> = Promise.resolve();
    let latestRequestedQuantity: number | undefined;

    /**
     * Serialized, latest-wins: a request slower than the debounce would overlap the
     * next edit, and two line-item updates can land out of order, leaving the server
     * on a quantity nobody asked for. The guard lives here rather than in the
     * consumer's `disabled` binding because `quantity` is a writable ref.
     */
    const updateQuantity = async (quantityInput: number | undefined) => {
        // This invocation settles whatever edit was pending.
        pendingQuantityEdit.value = false;

        latestRequestedQuantity = quantityInput;
        pendingQuantityWrites.value += 1;

        const write = quantityWriteQueue.then(async () => {
            // Superseded while queued, or already what the server holds.
            if (latestRequestedQuantity !== quantityInput) return;
            if (quantityInput === itemQuantity.value) return;

            try {
                await commitQuantity(Number(quantityInput));

                // The server may clamp, so resync rather than trusting the request.
                quantity.value = itemQuantity.value;

                onCommitted?.(quantity.value);
            } catch (error) {
                // `commitQuantity` handles its own request errors; anything reaching
                // here would surface as an unhandled rejection from the debounce.
                log.error('[Cart] Failed to update the line item quantity', { quantityInput, error });
            }
        });

        // A rejected link must not block the writes behind it.
        quantityWriteQueue = write.catch(() => {});

        try {
            await write;
        } finally {
            pendingQuantityWrites.value -= 1;
        }
    };

    const debounceUpdate = useDebounceFn(updateQuantity, debounceMs);

    watch(quantity, () => {
        // Busy from the first diverging keystroke, however many follow.
        if (quantity.value !== itemQuantity.value) {
            pendingQuantityEdit.value = true;
        }

        // Re-arm even for a no-op: reverting must overwrite the pending call's
        // argument, or the reverted value is still sent.
        debounceUpdate(quantity.value);
    });

    return { quantity, isUpdating, isBusy };
};
